import { ChangeEventHandler, useCallback, useEffect, useMemo, useState } from 'react';

import { addStyles } from '../../utils/helpers';

type SegmentedButtonData = {
  label: string;
  value: string;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>;
};

type SegmentedButtonSize = 'medium' | 'large';

export interface SegmentedButtonProps {
  /** The choices for the segmented button */
  data: SegmentedButtonData[];
  /** The (html) name used for forms */
  name: string;
  /** The callback for value changes */
  onChange: (selected: string | string[]) => void;
  /** The type of the segmented button. Radio only allows one seletion
   * while checkbox allows multiple selections
   */
  type?: 'radio' | 'checkbox';
  /** The selected value(s) */
  value?: string[];
  /** The size of the segmented button */
  size?: SegmentedButtonSize;
  /** DEPRECATED. The class name for the button */
  buttonClassName?: string;
  /** The class name for the icons */
  iconClassName?: string;
}

const getButtonStyle = (index: number, length: number, selected: boolean): string => {
  const styles = [];
  if (index === 0) {
    styles.push('rounded-l-lg');
  } else if (index == length - 1) {
    styles.push('rounded-r-lg');
  }
  if (selected) {
    styles.push('bg-jungle-50', 'border-jungle-900', 'hover:bg-jungle-100', 'z-10');
  } else {
    styles.push('border-gray-500', 'bg-white', 'hover:text-primary');
  }

  return styles.join(' ');
};

const iconSizes = {
  medium: 'h-6 w-4',
  large: 'h-6 w-6',
};

const getIconStyle = (index: number, selected: boolean, indexOfHovered: number, size: SegmentedButtonSize): string =>
  index === indexOfHovered && !selected ? `${iconSizes[size]} fill-primary` : iconSizes[size];

const baseLabelStyle =
  'flex peer-checked:text-jungle-900 peer-checked:fill-jungle-900 cursor-pointer select-none justify-center';
const labelStyles = {
  medium: `${baseLabelStyle} px-2 py-1`,
  large: `${baseLabelStyle}  px-5 py-2.5`,
};

/*eslint-disable-next-line max-lines-per-function */
export const SegmentedButton = (props: SegmentedButtonProps) => {
  const { data, name, type = 'radio', size = 'large', onChange, value, buttonClassName, iconClassName } = props;
  const [selected, setSelected] = useState<string[]>([]);
  const [indexOfHovered, setIndexOfHovered] = useState<number>(-1);

  useEffect(() => {
    if (!value) return;
    setSelected(value);
  }, [value]);

  const handleRadioChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      event.stopPropagation();
      const { value } = event.target;
      if (onChange) onChange(value);
      setSelected([value]);
    },
    [onChange]
  );

  const handleCheckboxChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      event.stopPropagation();
      const { value, checked } = event.target;
      const newSelected = checked ? [...selected, value] : selected.filter((v) => v !== value);

      if (onChange) onChange([...newSelected]);
      setSelected([...newSelected]);
    },
    [selected, onChange]
  );

  const onChangeHandler = useMemo(() => {
    return type === 'checkbox' ? handleCheckboxChange : handleRadioChange;
  }, [type, handleCheckboxChange, handleRadioChange]);

  return (
    <fieldset className="m-0">
      <div className="flex flex-row rounded-lg">
        {data.map((item, index) => {
          const { value, label, icon } = item;
          const checked = selected.includes(value);
          return (
            <div
              key={value}
              className={addStyles([
                'border border-solid flex items-center justify-center',
                buttonClassName,
                getButtonStyle(index, data.length, selected.includes(value)),
              ])}
              onMouseEnter={() => setIndexOfHovered(index)}
              onMouseLeave={() => setIndexOfHovered(-1)}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type={type}
                value={value}
                name={name}
                id={value}
                className={addStyles(['hidden', 'peer'])}
                onChange={onChangeHandler}
                checked={checked}
              />
              <label htmlFor={value} className={labelStyles[size]}>
                {icon && (
                  <div className={'pr-2 ' + iconClassName}>
                    {icon({ className: getIconStyle(index, selected.includes(value), indexOfHovered, size) })}
                  </div>
                )}{' '}
                {label}
              </label>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
};
export default SegmentedButton;
